import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import QuestionCard from "./QuestionCard";
import { getOptionLabel } from "./questionOptions";
import {
  SUFFIX_SEP,
  TRIP_SEP,
  evalCondition,
  getMultiselectDependency,
} from "./displayConditionUtils";

const Division = ({
  isEmployee,
  division,
  answers,
  setAnswers,
  handleAnswerChange,
  questionsList,
  optionsCache,
  setOptionsCache,
  onQuestionLoaded,
  expandAll,
  fillFrom,
}) => {
  const divisionName =
    division?.divisionTitle || division?.name || "Untitled Division";
  const divisionId = division?.divisionId || division?._id || "";
  const questions = division?.questions || [];
  const [open, setOpen] = useState(true);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      setOpen(true);
      return;
    }

    if (expandAll !== undefined || fillFrom !== undefined) {
      setOpen(!!(expandAll || fillFrom));
    }
  }, [expandAll, fillFrom]);

  const renderChildren = useCallback(
    (
      parentId,
      depth,
      tripIndex,
      optionSuffix = null,
      repeatingParentId = null,
      indexPath,
    ) => {
      return (questionsList || [])
        .filter((child) => {
          // Fast-path: ONLY the non-capital goods table is evaluated as a direct child
          // of scope3_first so it appears at the correct visual level (1.2).
          // evalCondition handles its visibility: shows when scope3_categories has items and capital_goods NOT among them
          if (
            child.questionId === "scope3_categories_non_capital_goods_table" &&
            parentId === "scope3_first"
          ) {
            return evalCondition(child, answers, {
              tripIndex,
              optionSuffix,
              repeatingParentId,
            });
          }

          // Explicitly prevent the non-capital goods table from rendering as a child
          // of the multiselect (1.1.1). It should only render via the fast-path above (as 1.2).
          if (
            child.questionId === "scope3_categories_non_capital_goods_table" &&
            parentId === "scope3_categories"
          ) {
            return false;
          }

          if (!child.displayCondition) return false;

          const parentConditions = child.displayCondition.filter(
            (c) => (c.triggerQuestionId || c.triggerFieldId) === parentId,
          );

          if (parentConditions.length === 0) return false;

          const logicalOp = (child.logicalOperator || "AND").toUpperCase();

          const isSatisfied = (c) => {
            const triggerId = c.triggerQuestionId || c.triggerFieldId;
            const res = evalCondition(c, answers, {
              tripIndex,
              optionSuffix,
              repeatingParentId,
            });
            if (triggerId === "scope3_categories") {
              console.log(
                `Division Visibility Check - Trigger: ${triggerId}, Result: ${res}`,
              );
            }
            return res;
          };

          if (parentConditions.length === 1) {
            return isSatisfied(parentConditions[0]);
          }

          if (
            logicalOp === "OR" &&
            parentConditions.every((c) => c.operator === "exists")
          ) {
            return parentConditions.every(isSatisfied);
          }

          if (logicalOp === "OR") return parentConditions.some(isSatisfied);
          return parentConditions.every(isSatisfied);
        })
        .map((child, idx) => {
          let childSuffix = optionSuffix;

          if (child.displayCondition) {
            const specificCond = child.displayCondition.find(
              (c) =>
                (c.triggerQuestionId || c.triggerFieldId) === parentId &&
                c.operator === "eq" &&
                typeof c.value === "string",
            );

            if (specificCond) {
              childSuffix = optionSuffix
                ? `${optionSuffix}${SUFFIX_SEP}${specificCond.value}`
                : specificCond.value;
            }
          }

          return renderNode(
            child,
            depth,
            tripIndex,
            childSuffix,
            repeatingParentId,
            `${indexPath}.${idx + 1}`,
          );
        });
    },
    [questionsList, answers],
  );

  const renderNode = useCallback(
    (
      question,
      depth = 0,
      tripIndex = null,
      optionSuffix = null,
      repeatingParentId = null,
      indexPath = "1",
    ) => {
      const isVisible = evalCondition(question, answers, {
        tripIndex,
        optionSuffix,
        repeatingParentId,
      });

      if (!isVisible) return null;

      const multiselectDependency = getMultiselectDependency(
        question,
        answers,
        questionsList,
        tripIndex,
        optionSuffix,
      );

      const alreadyScopedToCurrentOption =
        !!optionSuffix &&
        multiselectDependency?.selectedItems?.some(
          (item) =>
            optionSuffix === item ||
            optionSuffix.endsWith(`${SUFFIX_SEP}${item}`),
        );

      if (
        multiselectDependency?.selectedItems?.length > 0 &&
        !alreadyScopedToCurrentOption
      ) {
        const actualSelectedItems = multiselectDependency.selectedItems.filter(
          (v) => v !== "",
        );

        return (
          <div
            key={`ms_${question.questionId}_${indexPath}`}
            className="space-y-4"
          >
            {actualSelectedItems.map((itemValue, idx) => {
              const nextSuffix = optionSuffix
                ? `${optionSuffix}${SUFFIX_SEP}${itemValue}`
                : itemValue;

              const itemLabel = getOptionLabel(
                multiselectDependency.triggerId,
                itemValue,
                optionsCache,
              );

              return (
                <div
                  key={`${question.questionId}_${itemValue}_${idx}`}
                  className="p-4 border border-teal-200 rounded-lg bg-teal-50/30 space-y-4"
                >
                  <div className="text-teal-700 font-bold mb-2 uppercase text-xs tracking-wider">
                    {itemLabel}
                  </div>
                  {renderNode(
                    question,
                    depth,
                    tripIndex,
                    nextSuffix,
                    repeatingParentId,
                    indexPath,
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      const originalQid = question.questionId;

      let optionLabel = "";
      if (optionSuffix) {
        const displaySuffix = optionSuffix.includes(SUFFIX_SEP)
          ? optionSuffix.substring(
              optionSuffix.lastIndexOf(SUFFIX_SEP) + SUFFIX_SEP.length,
            )
          : optionSuffix;

        const triggerQuestionId =
          multiselectDependency?.triggerId ||
          question.displayCondition?.find(
            (c) => c.triggerQuestionId || c.triggerFieldId,
          )?.triggerQuestionId ||
          question.displayCondition?.find(
            (c) => c.triggerQuestionId || c.triggerFieldId,
          )?.triggerFieldId;

        if (triggerQuestionId) {
          optionLabel = getOptionLabel(
            triggerQuestionId,
            displaySuffix,
            optionsCache,
          );
        } else {
          optionLabel = displaySuffix;
        }
      }

      let baseQid = originalQid;
      if (tripIndex !== null) baseQid += `${TRIP_SEP}${tripIndex}`;

      let fullQid = baseQid;
      if (optionSuffix) fullQid += `${SUFFIX_SEP}${optionSuffix}`;

      let lookupValue =
        answers[fullQid] !== undefined
          ? answers[fullQid]
          : answers[baseQid] !== undefined
            ? answers[baseQid]
            : question.value;

      // Extraction for object-based values
      if (
        lookupValue &&
        typeof lookupValue === "object" &&
        !Array.isArray(lookupValue)
      ) {
        lookupValue =
          lookupValue.value !== undefined
            ? lookupValue.value
            : lookupValue.answer !== undefined
              ? lookupValue.answer
              : lookupValue;
      }

      const numberValue = parseInt(lookupValue || 0, 10);
      const qid = answers[fullQid] !== undefined ? fullQid : baseQid;

      return (
        <div
          key={qid}
          className={
            depth > 0
              ? "ml-6 rounded-lg mt-3 border-l-4 border-l-teal-500"
              : "mb-6"
          }
        >
          <QuestionCard
            isEmployee={isEmployee}
            question={question}
            displayIndex={indexPath}
            answers={answers}
            setAnswers={setAnswers}
            handleAnswerChange={handleAnswerChange}
            optionsCache={optionsCache}
            setOptionsCache={setOptionsCache}
            tripIndex={tripIndex}
            optionSuffix={optionSuffix}
            optionLabel={optionLabel}
            onQuestionLoaded={onQuestionLoaded}
            divisionId={divisionId}
          />

          {(question.questionType === "number" ||
            originalQid === "number_of_trip_select") &&
          numberValue > 0 &&
          (questionsList || []).some((child) =>
            child.displayCondition?.some(
              (c) => (c.triggerQuestionId || c.triggerFieldId) === originalQid,
            ),
          ) ? (
            <div className="mt-6 space-y-6">
              {Array.from({ length: Math.min(numberValue, 100) }, (_, i) => (
                <div
                  key={`${qid}_loop_${i}`}
                  className="p-4 border-2 border-teal-100 rounded-xl bg-teal-50/10"
                >
                  <div className="text-teal-600 font-bold mb-4 uppercase text-sm tracking-wider">
                    {originalQid.includes("trip") ||
                    originalQid.includes("journey")
                      ? "Trip"
                      : "Entry"}{" "}
                    {i + 1}
                  </div>

                  {renderChildren(
                    originalQid,
                    depth + 1,
                    i,
                    optionSuffix,
                    originalQid,
                    indexPath,
                  )}
                </div>
              ))}
            </div>
          ) : (
            renderChildren(
              originalQid,
              depth + 1,
              tripIndex,
              optionSuffix,
              repeatingParentId,
              indexPath,
            )
          )}
        </div>
      );
    },
    [
      answers,
      optionsCache,
      questionsList,
      setAnswers,
      setOptionsCache,
      handleAnswerChange,
      onQuestionLoaded,
      divisionId,
      renderChildren,
    ],
  );

  const rootQuestions = questions.filter(
    (q) => !q?.displayCondition || q.displayCondition.length === 0,
  );

  return (
    <div className="mb-6">
      <div
        className={`w-full flex items-center justify-between px-6 py-4 text-left transition-colors duration-200 ${
          open ? "bg-teal-50" : "hover:bg-gray-50 bg-gray-50"
        }`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <h2
          className={`font-semibold text-lg ${open ? "text-teal-700" : "text-gray-700"}`}
        >
          {divisionName}
        </h2>
        <ChevronDown
          size={18}
          className={`font-semibold text-lg ${
            open ? "rotate-180 text-[#1D8F89]" : "text-gray-700"
          }`}
        />
      </div>

      {open && (
        <div className="p-8 bg-white border-x border-b border-gray-100 rounded-b-2xl shadow-sm">
          <div className="space-y-8">
            {rootQuestions.map((question, index) =>
              renderNode(question, 0, null, null, null, `${index + 1}`),
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Division;
